// Turns an axios error into a message that tells the user what actually went
// wrong (no internet, server down, timeout, server error...) instead of a
// generic "Something went wrong".

const STATUS_MESSAGES = {
  400: "The request was invalid. Please check the details and try again.",
  401: "Your session has expired or the credentials are invalid.",
  403: "You don't have permission to perform this action.",
  404: "The requested resource was not found on the server.",
  408: "The server took too long to respond. Please try again.",
  409: "This conflicts with existing data. Please refresh and try again.",
  413: "The data you sent is too large.",
  422: "Some of the details are invalid. Please check and try again.",
  429: "Too many requests. Please wait a moment and try again.",
  500: "The server ran into an internal error. Please try again or contact support.",
  502: "The server is unreachable right now (bad gateway). Please try again shortly.",
  503: "The server is temporarily unavailable (maintenance or overload). Please try again shortly.",
  504: "The server took too long to respond (gateway timeout). Please try again.",
};

const backendMessage = (data) => {
  if (!data || typeof data !== "object") return null;
  const message = data.message ?? data.error ?? data.detail;
  return typeof message === "string" && message.trim() ? message : null;
};

export const getApiErrorMessage = (error) => {
  if (error?.code === "ERR_CANCELED") return "The request was cancelled.";

  if (!error?.response) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return "No internet connection. Please check your network and try again.";
    }
    if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
      return "The server took too long to respond. Please check your connection and try again.";
    }
    return "Unable to reach the server. It may be down — please try again shortly or contact support.";
  }

  const { status, data } = error.response;
  const fromBackend = backendMessage(data);
  if (fromBackend) return fromBackend;
  if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
  if (status >= 500) return `The server returned an error (${status}). Please try again or contact support.`;
  return `Request failed (${status}). Please try again.`;
};

// Makes sure `error.response.data.message` always holds a meaningful message,
// so existing callers reading that path show the real cause. Errors with no
// response (offline, server down, timeout) get a synthetic response with
// status 0.
export const attachApiErrorMessage = (error) => {
  const message = getApiErrorMessage(error);
  error.userMessage = message;

  if (!error.response) {
    error.response = { status: 0, data: { message }, config: error.config };
  } else if (!backendMessage(error.response.data)) {
    error.response.data =
      error.response.data && typeof error.response.data === "object"
        ? { ...error.response.data, message }
        : { message };
  }
  return error;
};
